/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { OutlinedInput, Table, TableBody, TableContainer } from '@mui/material';

import { useEventCallback } from '../../../../../../components/hooks/useEventCallback';
import { ArrowRoundedIcon } from '../../../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../../../i18n';
import { RootState } from '../../../../../../store';
import { EntityId } from '../../../../../../types/api/EntityId';
import { ClusterAccessor } from '../../../../../../types/common/Accessors';
import { DatasetImport } from '../../../../../../types/dataset/DatasetImport';
import { huSessionStorage } from '../../../../../../utils/huSessionStorage';
import { useObjectState } from '../../../../../../utils/useObjectState';
import {
  CancelDatasetDataType,
  DeleteDatasetDataType,
  ReimportDatasetDataType,
  SetDatasetNotReadonlyDataType,
} from '../../DatasetsTab.types';
import { DatasetImportsTableDatabase } from './components/DatasetImportsTableDatabase/DatasetImportsTableDatabase';
import { DatasetImportsTableHead } from './components/DatasetImportsTableHead/DatasetImportsTableHead';
import { DatasetImportsTableContext, DatasetImportsTableContextType } from './DatasetImportsTable.context';
import { useDatasetImportsTableStyles } from './DatasetImportsTable.styles';
import { DatasetImportsTablePersistingState, GroupedDatasets } from './DatasetImportsTable.types';

type DatasetImportsTableProps = {
  datasetImports: DatasetImport[];
  handleCancelDatasetData: (props: CancelDatasetDataType) => void;
  handleReimportDatasetData: (props: ReimportDatasetDataType) => void;
  handleDeleteDatasetData: (props: DeleteDatasetDataType) => void;
  handleSetNotReadonlyDatasetData: (props: SetDatasetNotReadonlyDataType) => void;
  containerRef?: React.RefObject<any>;
  clusterAccessor: ClusterAccessor;
};

const getStorageKey = ({ identifier, userId }: { userId: EntityId; identifier: string }) => {
  return `${userId}_${identifier}_ditcs`;
};

const initialPersistingState: DatasetImportsTablePersistingState = {
  collapseDatabases: {},
  collapseSchemas: {},
  selectedDatabase: '',
};

export function DatasetImportsTable(props: DatasetImportsTableProps) {
  const { t } = useTranslation(Namespaces.Cluster);
  const classes = useDatasetImportsTableStyles();
  const auth = useSelector((state: RootState) => state.auth);

  const {
    datasetImports,
    handleCancelDatasetData,
    handleReimportDatasetData,
    handleDeleteDatasetData,
    handleSetNotReadonlyDatasetData,
    containerRef,
    clusterAccessor,
  } = props;

  const [persistingState, setPersistingState] = useObjectState<DatasetImportsTablePersistingState>(() => {
    return (
      huSessionStorage.get<DatasetImportsTablePersistingState>(
        getStorageKey({
          userId: auth.user!.id,
          identifier: clusterAccessor.clusterId,
        }),
      ) ?? initialPersistingState
    );
  });

  const { selectedDatabase } = persistingState;

  const { allDatabases, preparedImports } = useMemo<{
    preparedImports: GroupedDatasets;
    allDatabases: string[];
  }>(() => {
    const groupedImports = _.groupBy(datasetImports, 'database');
    const preparedImports = _.chain(groupedImports)
      .map((imports, databaseName) => ({ imports, databaseName }))
      .orderBy('databaseName')
      .value();
    const result = {
      preparedImports,
      allDatabases: _.map(preparedImports, 'databaseName'),
    };
    if (selectedDatabase) {
      result.preparedImports = result.preparedImports.filter(v => v.databaseName === selectedDatabase);
    }
    return result;
  }, [datasetImports, selectedDatabase]);

  useEffect(() => {
    huSessionStorage.set<DatasetImportsTablePersistingState>(
      getStorageKey({
        userId: auth.user!.id,
        identifier: clusterAccessor.clusterId,
      }),
      persistingState,
      300,
    );
  }, [auth.user, clusterAccessor.clusterId, persistingState]);

  useEffect(() => {
    if (allDatabases.indexOf(selectedDatabase ?? '') === -1) {
      setPersistingState({ selectedDatabase: null });
    }
  }, [allDatabases, selectedDatabase, setPersistingState]);

  const getSchemaCollapseKey = useCallback(
    (schemaName: string, databaseName: string) => [schemaName, databaseName].join('_'),
    [],
  );

  const toggleDatabaseCollapsed = useEventCallback((databaseName: string) => {
    setPersistingState({
      collapseDatabases: {
        ...persistingState.collapseDatabases,
        [databaseName]: !persistingState.collapseDatabases[databaseName],
      },
    });
  });

  const toggleSchemaCollapsed = useEventCallback((schemaName: string, databaseName: string) => {
    const key = getSchemaCollapseKey(schemaName, databaseName);
    setPersistingState({
      collapseSchemas: {
        ...persistingState.collapseSchemas,
        [key]: !persistingState.collapseSchemas[key],
      },
    });
  });

  const providerValue: DatasetImportsTableContextType = {
    ...persistingState,
    clusterAccessor,
    toggleDatabaseCollapsed,
    toggleSchemaCollapsed,
    handleCancelDatasetData: useEventCallback(handleCancelDatasetData),
    handleReimportDatasetData: useEventCallback(handleReimportDatasetData),
    handleDeleteDatasetData: useEventCallback(handleDeleteDatasetData),
    handleSetNotReadonlyDatasetData: useEventCallback(handleSetNotReadonlyDatasetData),
    isCollapsedDatabase: useCallback(
      databaseName => Boolean(persistingState.collapseDatabases[databaseName]),
      [persistingState.collapseDatabases],
    ),
    isCollapsedSchema: useCallback(
      (schemaName, databaseName) =>
        Boolean(persistingState.collapseSchemas[getSchemaCollapseKey(schemaName, databaseName)]),
      [getSchemaCollapseKey, persistingState.collapseSchemas],
    ),
  };

  const { Provider } = DatasetImportsTableContext;

  return (
    <Provider value={providerValue}>
      <HUAutocomplete
        className={classes.databaseInput}
        popupIcon={<ArrowRoundedIcon />}
        renderInput={renderParams => (
          <OutlinedInput
            {...destructRenderInputParams(renderParams)}
            name="database"
            placeholder={t('details.datasets.table.database')}
          />
        )}
        options={allDatabases}
        openOnFocus={true}
        value={persistingState.selectedDatabase}
        onChange={(_e: any, value: string | null) => {
          setPersistingState({ selectedDatabase: value });
        }}
      />
      <TableContainer ref={containerRef} className={classes.container}>
        <Table className={classes.root}>
          <DatasetImportsTableHead />
          <TableBody>
            {_.map(preparedImports, ({ databaseName, imports }) => (
              <DatasetImportsTableDatabase key={databaseName} databaseName={databaseName} datasetImports={imports} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Provider>
  );
}
